import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useGenerateIdentityQr } from "@orbit/api-client-react";

/**
 * The SUKI card. Tapping the floating "Show my card" button on /cards
 * mounts this modal; it requests a fresh 60-second token immediately
 * and keeps a countdown going. A new token is fetched 5 seconds before
 * expiry so the visible QR never goes stale on the merchant counter.
 *
 * D114 — clock-skew-proof scheduling. We used to derive both the
 * countdown and the next-refresh delay from `expiresAt - Date.now()`,
 * which collapsed the refresh interval to the 1-second floor on any
 * device whose clock was even ~54s ahead of the server (observed in
 * the wild for fresh signups on phones with mis-set time → the QR
 * "refreshed every 0s"). Now the server returns `ttlSec`; we anchor
 * the deadline on the client's own `Date.now()` so refresh cadence
 * is independent of clock skew.
 */
const REFRESH_LEAD_MS = 5_000;

interface Props {
  open: boolean;
  onClose: () => void;
  customerName: string | null;
}

export function IdentityQrModal({ open, onClose, customerName }: Props) {
  const generate = useGenerateIdentityQr();
  const [token, setToken] = useState<string | null>(null);
  const [deadline, setDeadline] = useState<Date | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(60);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- mint a fresh token when the modal opens, and again before expiry
  useEffect(() => {
    if (!open) {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
      setToken(null);
      setDeadline(null);
      return;
    }

    let cancelled = false;
    const fetchOne = async () => {
      try {
        const r = await generate.mutateAsync();
        if (cancelled) return;
        // Anchor the deadline on the client clock using the server's
        // ttl, NOT the server's `expiresAt` ISO string. See D114.
        const ttlMs = r.ttlSec * 1000;
        const localDeadline = new Date(Date.now() + ttlMs);
        setToken(r.qrToken);
        setDeadline(localDeadline);
        const lead = Math.max(1_000, ttlMs - REFRESH_LEAD_MS);
        if (refreshTimer.current) clearTimeout(refreshTimer.current);
        refreshTimer.current = setTimeout(fetchOne, lead);
      } catch {
        // Surfaced via generate.isError below — leave the user with
        // the previous token (if any) until they retry.
      }
    };
    void fetchOne();
    return () => {
      cancelled = true;
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    };
    // We intentionally re-run only on `open` — `generate` is stable per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ---- 1Hz countdown for the visible badge
  useEffect(() => {
    if (!deadline) return;
    const tick = () => {
      const ms = deadline.getTime() - Date.now();
      setSecondsLeft(Math.max(0, Math.ceil(ms / 1000)));
    };
    tick();
    const i = setInterval(tick, 1_000);
    return () => clearInterval(i);
  }, [deadline]);

  // ---- close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Your Orbit card"
      className="fixed inset-0 z-50 flex items-end justify-center bg-navy/40 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-t-3xl bg-white px-6 pb-10 pt-8 shadow-2xl sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Large close button at top-right */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 flex h-11 w-11 items-center justify-center rounded-full text-navy hover:bg-navy/5 active:bg-navy/10"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.2}
            className="h-6 w-6"
            aria-hidden="true"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <div className="mx-auto h-1.5 w-10 rounded-full bg-ink-mid sm:hidden" />
        <header className="mt-4 text-center">
          <p className="text-xs font-semibold uppercase tracking-wider text-amber">
            Your Orbit Card
          </p>
          <h2 className="mt-1 text-2xl font-bold text-navy">
            {customerName ? `Hi ${customerName}` : "Show this at the counter"}
          </h2>
          <p className="mt-1 text-sm text-ink-dark">
            One card. Many merchants.
          </p>
        </header>

        <div className="mt-6 flex flex-col items-center">
          <div className="rounded-card border border-navy/10 bg-white p-5 shadow-sm">
            {token ? (
              <QRCodeSVG
                value={token}
                size={232}
                level="M"
                marginSize={0}
                bgColor="#FFFFFF"
                fgColor="#0F1E3D"
                aria-label="Identity QR code"
              />
            ) : generate.isError ? (
              <div className="flex h-[232px] w-[232px] flex-col items-center justify-center text-center">
                <p className="text-red-600 font-medium">Couldn't load your card.</p>
                <button
                  className="mt-3 text-navy underline text-sm"
                  onClick={() => void generate.mutateAsync()}
                >
                  Try again
                </button>
              </div>
            ) : (
              <div className="flex h-[232px] w-[232px] items-center justify-center">
                <span className="animate-pulse text-ink-dark">Generating…</span>
              </div>
            )}
          </div>

          {token && (
            <p className="mt-4 text-sm text-ink-dark" aria-live="polite">
              Refreshes in <span className="font-semibold text-navy">{secondsLeft}s</span>…
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
